import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import {
  usePreviewRunEventQuery,
  usePreviewRunServicesQuery,
  type PreviewRunServices,
  type PreviewServiceCall,
} from "@/api/previewInsights";
import {
  LAB_BADGE_HELP,
  LAB_BADGE_LABELS,
  LAB_POSITION_HELP,
  alignLabRuns,
  type LabAlignedTurn,
  type LabPendingLine,
  type LabBadgeKind,
  type LabRunData,
  type LabTurn,
  type LabTurnBadge,
  type LabTurnDiff,
} from "@/lib/labModel";
import { cn } from "@/lib/cn";
import {
  readContentSlot,
  readInlineBody,
  type ContentSlot,
} from "@/lib/previewDecisions";
import { EM_DASH, ISRAEL_TIME_NOTE, formatIsraelTime } from "@/lib/format";
import { recallRun } from "@/lib/labRunMemory";
import { TEXT_READ_GAP_MS } from "@/lib/labThrottle";
import {
  callsForOrdinal,
  concernTone,
  safetyCallOf,
  safetyCategories,
  turnOrdinal,
} from "@/lib/labServices";
import { SHOW_TEXT_KEY, readFlag, writeFlag } from "@/lib/labSpeech";
import { describeView } from "@/lib/previewInsights";
import { SIM_STATUS_LABELS } from "@/lib/previewSim";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Skeleton } from "@/ui/Skeleton";
import { DeniedState, EmptyState, ErrorState, LoadingState } from "@/ui/states";

import { Line, describeDetailFailure } from "../preview/PreviewRunConversation";
import { LabContinueFromTurn } from "./LabContinueFromTurn";
import {
  LabVoiceProvider,
  PlayLineButton,
  SpeakerToggle,
  useLabVoice,
} from "./LabVoice";

/**
 * Lab, centre pane: the selected run's turns as a chat, or two runs side by
 * side aligned by turn number.
 *
 * Text is shown by default (every Lab run is synthetic preview data); a small
 * `Hide text` switch turns it off and the choice is remembered in this
 * browser. Each turn reads its own body through the one body-bearing event
 * read, nearby turns first and at most two reads at a time, one start per second, so a long run
 * does not flood the service. Bodies are rendered as text with `dir="auto"`;
 * nothing is stored or logged here.
 */

/** How many turn-text reads may be in flight at once. */
export const TEXT_READ_CONCURRENCY = 2;

export { TEXT_READ_GAP_MS };

type TextGate = {
  /** Asks for a read slot for this turn (idempotent). */
  request: (id: string) => void;
  /** Gives the slot back once the read settled. */
  release: (id: string) => void;
  /** True once the turn holds, or has held, a slot. */
  granted: (id: string) => boolean;
  /** Turns after this number are new replies of a live session; null when none are. */
  announceAfter: number | null;
};

const TextGateContext = createContext<TextGate | null>(null);

/** A first-come queue that lets `limit` turn reads run at once. */
function useTextGate(limit: number, announceAfter: number | null): TextGate {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const state = useRef({
    active: new Set<string>(),
    done: new Set<string>(),
    queue: [] as string[],
    lastStart: 0,
    timer: null as number | null,
  });
  const pumpAgain = useRef<() => void>(() => undefined);
  // Starts queued reads while a slot is free, one per TEXT_READ_GAP_MS.
  const pump = useCallback(() => {
    const s = state.current;
    while (s.active.size < limit && s.queue.length > 0) {
      const wait = s.lastStart + TEXT_READ_GAP_MS - Date.now();
      if (wait > 0) {
        s.timer ??= window.setTimeout(() => {
          s.timer = null;
          pumpAgain.current();
          rerender();
        }, wait);
        return;
      }
      s.active.add(s.queue.shift() as string);
      s.lastStart = Date.now();
    }
  }, [limit]);
  useEffect(() => {
    pumpAgain.current = pump;
  }, [pump]);
  useEffect(
    () => () => {
      if (state.current.timer !== null) {
        window.clearTimeout(state.current.timer);
        state.current.timer = null;
      }
    },
    [],
  );
  const request = useCallback(
    (id: string) => {
      const { active, done, queue } = state.current;
      if (active.has(id) || done.has(id) || queue.includes(id)) {
        return;
      }
      queue.push(id);
      pump();
      rerender();
    },
    [pump],
  );
  const release = useCallback(
    (id: string) => {
      const { active, done, queue } = state.current;
      const index = queue.indexOf(id);
      if (index >= 0) {
        queue.splice(index, 1);
      }
      if (!active.delete(id)) {
        return;
      }
      done.add(id);
      pump();
      rerender();
    },
    [pump],
  );
  const granted = useCallback(
    (id: string) => state.current.active.has(id) || state.current.done.has(id),
    [],
  );
  // A fresh object per render: consumers read `granted` (a ref) while rendering.
  return { request, release, granted, announceAfter };
}

const KIND_ORDER: readonly LabBadgeKind[] = [
  "classification",
  "threshold",
  "routing",
  "transition",
  "safety",
];

export function LabConversation({
  projectId,
  primary,
  compare,
  selectedTurn,
  onSelectTurn,
  scrollSignal = 0,
  followLive = false,
  pendingLine = null,
  candidateId = null,
  onContinued,
}: {
  projectId: string;
  primary: LabRunData | null;
  compare: LabRunData | null;
  selectedTurn: number | null;
  onSelectTurn: (turnNumber: number) => void;
  /** Bumped when a selection made elsewhere (timeline, graph...) should bring its row into view again. */
  scrollSignal?: number;
  /** A line just sent that the run's events do not carry yet. */
  pendingLine?: LabPendingLine | null;
  /** "Follow live" is on: the newest turn stays in view as its text is read. */
  followLive?: boolean;
  /** The testing version "Continue from here" runs against (the top bar's). */
  candidateId?: string | null;
  /** Opens the new manual run; without it the action is not offered. */
  onContinued?: (previewRunRef: string) => void;
}) {
  return (
    <section
      aria-labelledby="lab-conversation-heading"
      className="flex min-w-0 flex-col gap-3"
    >
      <h2 id="lab-conversation-heading" className="sr-only">
        Conversation
      </h2>
      {primary === null ? (
        <EmptyState
          title="No active conversation"
          description="Choose the settings above and press New conversation, or open one from History."
        />
      ) : (
        <LabVoiceProvider
          projectId={projectId}
          previewRunRef={primary.previewRunRef}
          language={primary.manual?.language ?? undefined}
        >
          <ConversationPane
            key={primary.previewRunRef + (compare?.previewRunRef ?? "")}
            projectId={projectId}
            primary={primary}
            compare={compare}
            selectedTurn={selectedTurn}
            onSelectTurn={onSelectTurn}
            scrollSignal={scrollSignal}
            pendingLine={pendingLine}
            followLive={followLive}
            candidateId={candidateId}
            onContinued={onContinued}
          />
        </LabVoiceProvider>
      )}
    </section>
  );
}

function safetyCallFor(
  run: LabRunData,
  services: PreviewRunServices | undefined,
  turn: LabTurn,
): PreviewServiceCall | null | undefined {
  if (services === undefined) {
    return undefined;
  }
  return safetyCallOf(
    callsForOrdinal(services, turnOrdinal(run.lab.turns, turn.turnNumber)),
  );
}

function SafetyBubbleBadge({ call }: { call: PreviewServiceCall | null }) {
  if (call === null) {
    return (
      <Badge tone="outline" className="mt-1">
        Safety: not run
      </Badge>
    );
  }
  const categories = safetyCategories(call);
  return (
    <Badge tone={concernTone(call.outcome)} className="mt-1">
      {`Safety: ${[call.outcome, ...categories].join(" · ")}`}
    </Badge>
  );
}

function ConversationPane({
  projectId,
  primary,
  compare,
  selectedTurn,
  onSelectTurn,
  scrollSignal,
  pendingLine,
  followLive,
  candidateId,
  onContinued,
}: {
  projectId: string;
  primary: LabRunData;
  compare: LabRunData | null;
  selectedTurn: number | null;
  onSelectTurn: (turnNumber: number) => void;
  scrollSignal: number;
  pendingLine: LabPendingLine | null;
  followLive: boolean;
  candidateId: string | null;
  onContinued: ((previewRunRef: string) => void) | undefined;
}) {
  const [showText, setShowText] = useState(() => readFlag(SHOW_TEXT_KEY, true));
  const primaryText = showText;
  const compareText = showText;
  const list = useRef<HTMLOListElement>(null);

  const primaryReady = primary.contentState === "ready";
  // A reply that arrives after the run was first read is "new" (read aloud
  // when the speaker is on). Only a single live or manual session reads aloud.
  const lastTurn = primaryReady
    ? primary.lab.turns.reduce((max, turn) => Math.max(max, turn.turnNumber), 0)
    : null;
  const [baseline, setBaseline] = useState<number | null>(null);
  if (baseline === null && lastTurn !== null) {
    setBaseline(lastTurn);
  }
  const gate = useTextGate(
    TEXT_READ_CONCURRENCY,
    compare === null && (primary.live || primary.manual != null)
      ? baseline
      : null,
  );
  const compareReady = compare !== null && compare.contentState === "ready";
  // Read only once the run itself is readable: every services read goes upstream.
  const primaryServices = usePreviewRunServicesQuery(
    projectId,
    primary.previewRunRef,
    { live: primary.live, enabled: primaryReady },
  ).data;
  const compareServices = usePreviewRunServicesQuery(
    projectId,
    compare?.previewRunRef ?? null,
    { live: compare?.live ?? false, enabled: compareReady },
  ).data;
  const rows: LabAlignedTurn[] | null =
    compare === null
      ? null
      : alignLabRuns(
          primaryReady ? primary.lab.turns : [],
          compareReady ? compare.lab.turns : [],
        );
  // After Undo or Continue the replayed lines are not shown one by one: one line says the
  // conversation is being restored, and the turns appear when the replay is done.
  const restoring =
    compare === null &&
    (primary.manual?.replayStatus === "queued" ||
      primary.manual?.replayStatus === "running");
  const hasTurns =
    !restoring &&
    (rows !== null
      ? rows.length > 0
      : primaryReady && primary.lab.turns.length > 0);
  // Shown until the events carry the turn event it was sent for; other events
  // of that turn can arrive first and carry no patient text.
  const pending =
    compare === null &&
    pendingLine !== null &&
    pendingLine.previewRunRef === primary.previewRunRef &&
    !(primaryReady ? primary.lab.turns : []).some(
      (turn) =>
        turn.turnEvent !== null && turn.turnNumber >= pendingLine.expectedTurn,
    )
      ? pendingLine
      : null;
  const pendingReplying =
    pending !== null &&
    (pending.status === "sending" || primary.manual?.status === "busy");

  // A selection made anywhere (this list, ?turn=, another tab) brings its row
  // into view with a brief highlight; focus stays put.
  useEffect(() => {
    if (selectedTurn === null) {
      return;
    }
    const target = list.current?.querySelector<HTMLElement>(
      `[data-turn="${selectedTurn}"]`,
    );
    target?.scrollIntoView?.({ block: "nearest" });
    // The row is scrolled to while it still holds placeholders; Liv's reply is
    // read afterwards and grows it below the fold, so keep its END in view for
    // as long as this turn is followed (a reply can be taller than the pane).
    const observer =
      followLive && target && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scrollToEnd(target))
        : null;
    if (target) {
      observer?.observe(target);
    }
    // A data attribute (not state): React never re-renders the list for it.
    target?.setAttribute("data-flash", "");
    const timer = window.setTimeout(
      () => target?.removeAttribute("data-flash"),
      1400,
    );
    return () => {
      observer?.disconnect();
      window.clearTimeout(timer);
      target?.removeAttribute("data-flash");
    };
  }, [selectedTurn, scrollSignal, followLive]);

  // The sent line sits below the fold while Liv answers; follow it there.
  useEffect(() => {
    if (!followLive || pending === null) {
      return;
    }
    const line = list.current?.querySelector<HTMLElement>("[data-pending-line]");
    if (line) {
      scrollToEnd(line);
    }
  }, [pending, pendingReplying, followLive]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    const current = event.target;
    if (
      !(current instanceof HTMLElement) ||
      current.dataset["turnHeader"] === undefined
    ) {
      return;
    }
    const headers = Array.from(
      list.current?.querySelectorAll<HTMLElement>("[data-turn-header]") ?? [],
    );
    const next =
      headers[headers.indexOf(current) + (event.key === "ArrowDown" ? 1 : -1)];
    if (next) {
      event.preventDefault();
      next.focus();
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 text-sm">
          {compare === null ? (
            <RunHeading label={null} run={primary} />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <RunHeading label="Run A" run={primary} />
              <RunHeading label="Run B" run={compare} />
            </div>
          )}
        </div>
        {hasTurns ? (
          <div className="flex flex-wrap items-center gap-3">
            <SpeakerToggle />
            <Button
              variant="outline"
              size="sm"
              aria-pressed={showText}
              onClick={() => {
                writeFlag(SHOW_TEXT_KEY, !showText);
                setShowText(!showText);
              }}
            >
              {showText ? "Hide text" : "Show text"}
            </Button>
          </div>
        ) : null}
      </div>

      {restoring ? (
        <RestoringLine
          done={primary.manual?.turnsCompleted ?? 0}
          through={recallRun(primary.previewRunRef).restoreThrough ?? null}
        />
      ) : null}

      {compare === null ? (
        <RunNotices run={primary} />
      ) : (
        <>
          <RunNotices run={primary} label="Run A" />
          <RunNotices run={compare} label="Run B" />
        </>
      )}

      {compare === null && !primaryReady ? (
        <RunStateMessage run={primary} />
      ) : null}
      {compare !== null ? (
        <div className="grid grid-cols-2 gap-3">
          {!primaryReady ? <RunStateMessage run={primary} /> : <div />}
          {!compareReady ? <RunStateMessage run={compare} /> : <div />}
        </div>
      ) : null}

      {(compare === null ? primaryReady : primaryReady || compareReady) &&
      !hasTurns &&
      !restoring &&
      pending === null ? (
        <EmptyState
          title="No turns recorded yet"
          description="The conversation appears here once the runtime records a turn."
        />
      ) : null}

      {hasTurns || pending !== null ? (
        <TextGateContext.Provider value={gate}>
          <ol ref={list} aria-label="Turns" className="space-y-2">
            {rows === null
              ? primary.lab.turns.map((turn) => (
                  <li
                    key={turn.turnNumber}
                    data-turn={turn.turnNumber}
                    className={rowClass(turn.turnNumber === selectedTurn)}
                  >
                    <TurnHeader
                      turnNumber={turn.turnNumber}
                      selected={turn.turnNumber === selectedTurn}
                      onSelect={onSelectTurn}
                      onKeyDown={onKeyDown}
                    >
                      <TurnChips turn={turn} diff={null} />
                    </TurnHeader>
                    {onContinued !== undefined ? (
                      <div
                        className={cn(
                          "absolute end-3 top-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover/turn:opacity-100",
                          turn.turnNumber === selectedTurn && "opacity-100",
                        )}
                      >
                        <LabContinueFromTurn
                          dialog
                          projectId={projectId}
                          previewRunRef={primary.previewRunRef}
                          turnNumber={turn.turnNumber}
                          candidateId={candidateId}
                          onContinued={onContinued}
                        />
                      </div>
                    ) : null}
                    <TurnBubbles
                      projectId={projectId}
                      previewRunRef={primary.previewRunRef}
                      turn={turn}
                      showText={primaryText}
                      safetyCall={safetyCallFor(primary, primaryServices, turn)}
                    />
                  </li>
                ))
              : rows.map((row) => (
                  <AlignedRow
                    key={row.turnNumber}
                    row={row}
                    projectId={projectId}
                    primary={primary}
                    compare={compare as LabRunData}
                    primaryReady={primaryReady}
                    compareReady={compareReady}
                    selected={row.turnNumber === selectedTurn}
                    primaryText={primaryText}
                    compareText={compareText}
                    primaryServices={primaryServices}
                    compareServices={compareServices}
                    onSelectTurn={onSelectTurn}
                    onKeyDown={onKeyDown}
                  />
                ))}
            {pending !== null ? (
              <PendingLineRow
                line={pending}
                showText={primaryText}
                replying={pendingReplying}
              />
            ) : null}
          </ol>
        </TextGateContext.Provider>
      ) : null}
    </div>
  );
}

/**
 * Brings a followed row's end into view. The last row is scrolled to the very
 * bottom of its pane, because the composer is pinned over the pane's bottom edge.
 */
function scrollToEnd(row: HTMLElement) {
  row.scrollIntoView?.({ block: "end" });
  if (row.nextElementSibling !== null) {
    return;
  }
  let pane = row.parentElement;
  while (
    pane &&
    !(
      pane.scrollHeight > pane.clientHeight &&
      /(auto|scroll)/.test(getComputedStyle(pane).overflowY)
    )
  ) {
    pane = pane.parentElement;
  }
  const scroller = pane ?? document.scrollingElement;
  if (scroller) {
    scroller.scrollTop = scroller.scrollHeight;
  }
}

function RestoringLine({
  done,
  through,
}: {
  done: number;
  through: number | null;
}) {
  return (
    <div
      role="status"
      className="space-y-1 rounded-md border border-border bg-card p-3"
    >
      <p className="text-sm">
        {through === null
          ? "Restoring the conversation…"
          : `Restoring the conversation up to turn ${through}…`}
      </p>
      {through !== null ? (
        <div
          role="progressbar"
          aria-label="Restoring the conversation"
          aria-valuemin={0}
          aria-valuemax={through}
          aria-valuenow={Math.min(done, through)}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${Math.min(100, (done / through) * 100)}%` }}
          />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {done} {done === 1 ? "turn" : "turns"} replayed so far.
        </p>
      )}
    </div>
  );
}

function PendingLineRow({
  line,
  showText,
  replying,
}: {
  line: LabPendingLine;
  showText: boolean;
  replying: boolean;
}) {
  return (
    <li data-pending-line="" className={rowClass(false)}>
      <div className="me-auto max-w-[85%] rounded-lg bg-muted px-3 py-2">
        <p className="text-xs font-medium text-muted-foreground">Patient</p>
        {showText ? (
          <p
            dir="auto"
            className="whitespace-pre-wrap break-words text-sm text-foreground"
          >
            {line.text}
          </p>
        ) : null}
        {line.status === "failed" || line.status === "unconfirmed" ? (
          <p
            className={cn(
              "text-xs",
              line.status === "failed"
                ? "text-destructive"
                : "text-muted-foreground",
            )}
          >
            {line.status === "failed" ? "Not sent" : "Not confirmed"}
          </p>
        ) : null}
      </div>
      {replying ? (
        <div className="ms-auto max-w-[85%] rounded-lg bg-accent px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground">Liv</p>
          <p role="status" className="animate-pulse text-sm text-muted-foreground">
            Liv is replying…
          </p>
        </div>
      ) : null}
    </li>
  );
}

function rowClass(selected: boolean): string {
  return cn(
    "group/turn relative space-y-2 rounded-xl border bg-card p-3 shadow-sm transition-shadow data-[flash]:ring-2 data-[flash]:ring-ring",
    selected ? "border-primary bg-accent/40" : "border-border",
  );
}

function RunHeading({ label, run }: { label: string | null; run: LabRunData }) {
  const status = run.sim ? SIM_STATUS_LABELS[run.sim.status] : null;
  return (
    <div className="min-w-0 space-y-0.5">
      {label !== null ? (
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      ) : null}
      <p role="status" aria-live="polite" className="text-sm">
        {run.progress !== null ? (
          <span dir="ltr" className="font-medium">
            {run.progress}
          </span>
        ) : null}
        {status !== null ? (
          <span className="ms-2 text-muted-foreground">{status}</span>
        ) : null}
      </p>
      {run.sim?.status === "failed" && run.sim.error ? (
        <p dir="ltr" className="text-xs text-destructive">
          {run.sim.error}
        </p>
      ) : null}
    </div>
  );
}

/** Stale and partial-read notes for one run's page of events. */
function RunNotices({ run, label }: { run: LabRunData; label?: string }) {
  return (
    <>
      {run.stale ? (
        <p
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
        >
          {label ? `${label}: ` : ""}The last refresh failed. Showing the events
          read so far.
          <Button variant="outline" size="sm" onClick={run.refetch}>
            Refresh
          </Button>
        </p>
      ) : null}
      {run.contentState === "ready" && !run.complete ? (
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-foreground"
        >
          {label ? `${label}: ` : ""}Only the events read so far are shown: the
          run kept reporting more after its cursor stopped advancing, or after
          more events than the producer contract allows had been served.
        </p>
      ) : null}
      {run.contentState === "ready" && run.lab.unreadable > 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          {label ? `${label}: ` : ""}
          {run.lab.unreadable} record{run.lab.unreadable === 1 ? "" : "s"} on
          this run&apos;s event page could not be read by this build.
        </p>
      ) : null}
    </>
  );
}

function RunStateMessage({ run }: { run: LabRunData }) {
  switch (run.contentState) {
    case "pending":
      return <LoadingState label="Loading conversation" rows={3} />;
    case "denied":
      return (
        <DeniedState
          title="This preview run is not available to your account"
          description="Your account is not authorized to inspect preview runs in this project in this environment. Nothing about the run has been read."
        />
      );
    case "error":
      return (
        <ErrorState
          title="This preview run could not be read"
          onRetry={run.refetch}
        />
      );
    case "unavailable":
      return (
        <EmptyState
          title="No conversation recorded yet"
          description={`${run.view ? `${describeView(run.view)} ` : ""}Turns appear here once the runtime records one.`}
        />
      );
    case "ready":
      return null;
  }
}

function TurnHeader({
  turnNumber,
  selected,
  onSelect,
  onKeyDown,
  children,
}: {
  turnNumber: number;
  selected: boolean;
  onSelect: (turnNumber: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-turn-header=""
      aria-current={selected ? "true" : undefined}
      onClick={() => onSelect(turnNumber)}
      onKeyDown={onKeyDown}
      className="flex w-full flex-wrap items-center gap-2 rounded-sm text-start text-sm focus-visible:outline-2 focus-visible:outline-ring"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Turn <span dir="ltr">{turnNumber}</span>
      </span>
      {children}
    </button>
  );
}

function TurnChips({
  turn,
  diff,
}: {
  turn: LabTurn;
  diff: LabTurnDiff | null;
}) {
  return (
    <>
      <Badge tone="outline" dir="auto" title={LAB_POSITION_HELP}>
        {`${turn.agent ?? "—"} / ${turn.track ?? "—"}`}
        <span className="sr-only">{`, ${LAB_POSITION_HELP}`}</span>
      </Badge>
      {turn.badges.map((badge) => (
        <KindBadge
          key={badge.kind}
          badge={badge}
          differs={diff !== null && diff[badge.kind]}
        />
      ))}
      <TurnTime occurredAt={turn.turnEvent?.occurredAt ?? null} />
    </>
  );
}

function TurnTime({ occurredAt }: { occurredAt: string | null }) {
  const time = formatIsraelTime(occurredAt);
  return time === EM_DASH ? null : (
    <time
      dateTime={occurredAt ?? undefined}
      dir="ltr"
      title={ISRAEL_TIME_NOTE}
      className="text-xs text-muted-foreground"
    >
      {time}
    </time>
  );
}

function KindBadge({
  badge,
  differs,
}: {
  badge: LabTurnBadge;
  differs: boolean;
}) {
  const label = LAB_BADGE_LABELS[badge.kind];
  const attentionNote =
    badge.attention === 0
      ? ""
      : badge.kind === "threshold"
        ? `, ${badge.attention} not met`
        : `, ${badge.attention} need${badge.attention === 1 ? "s" : ""} attention`;
  return (
    <Badge
      title={LAB_BADGE_HELP[badge.kind]}
      tone={
        differs || badge.attention > 0
          ? badge.kind === "safety" && !differs
            ? "destructive"
            : "warning"
          : "accent"
      }
    >
      {label} <span dir="ltr">{badge.count}</span>
      {badge.attention > 0 ? (
        <span aria-hidden="true">
          {" "}
          (<span dir="ltr">{badge.attention}</span>)
        </span>
      ) : null}
      <span className="sr-only">
        {`, ${badge.count} event${badge.count === 1 ? "" : "s"}${attentionNote}${differs ? ", differs from the other run" : ""}. ${LAB_BADGE_HELP[badge.kind]}`}
      </span>
    </Badge>
  );
}

function AlignedRow({
  row,
  projectId,
  primary,
  compare,
  primaryReady,
  compareReady,
  selected,
  primaryText,
  compareText,
  primaryServices,
  compareServices,
  onSelectTurn,
  onKeyDown,
}: {
  row: LabAlignedTurn;
  projectId: string;
  primary: LabRunData;
  compare: LabRunData;
  primaryReady: boolean;
  compareReady: boolean;
  selected: boolean;
  primaryText: boolean;
  compareText: boolean;
  primaryServices: PreviewRunServices | undefined;
  compareServices: PreviewRunServices | undefined;
  onSelectTurn: (turnNumber: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}) {
  const differing = row.diff?.any
    ? KIND_ORDER.filter((kind) => row.diff?.[kind]).map((kind) =>
        LAB_BADGE_LABELS[kind].toLowerCase(),
      )
    : [];
  return (
    <li
      data-turn={row.turnNumber}
      className={cn(
        rowClass(selected),
        differing.length > 0 && "border-s-4 border-s-warning",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <TurnHeader
          turnNumber={row.turnNumber}
          selected={selected}
          onSelect={onSelectTurn}
          onKeyDown={onKeyDown}
        />
        {differing.length > 0 ? (
          <span className="text-xs font-medium text-foreground">
            Differs: {differing.join(", ")}
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Cell
          side="Run A"
          turn={row.a}
          ready={primaryReady}
          turnNumber={row.turnNumber}
          diff={row.diff}
          projectId={projectId}
          run={primary}
          showText={primaryText}
          services={primaryServices}
        />
        <Cell
          side="Run B"
          turn={row.b}
          ready={compareReady}
          turnNumber={row.turnNumber}
          diff={row.diff}
          projectId={projectId}
          run={compare}
          showText={compareText}
          services={compareServices}
        />
      </div>
    </li>
  );
}

function Cell({
  side,
  turn,
  ready,
  turnNumber,
  diff,
  projectId,
  run,
  showText,
  services,
}: {
  side: string;
  turn: LabTurn | null;
  ready: boolean;
  turnNumber: number;
  diff: LabTurnDiff | null;
  projectId: string;
  run: LabRunData;
  showText: boolean;
  services: PreviewRunServices | undefined;
}) {
  if (!ready) {
    return <div />;
  }
  if (turn === null) {
    return (
      <p className="rounded-md bg-muted/40 p-2 text-sm text-muted-foreground">
        No turn <span dir="ltr">{turnNumber}</span> in this run
        <span className="sr-only"> ({side})</span>
      </p>
    );
  }
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="sr-only">{side}</span>
        <TurnChips turn={turn} diff={diff} />
      </div>
      <TurnBubbles
        projectId={projectId}
        previewRunRef={run.previewRunRef}
        turn={turn}
        showText={showText}
        safetyCall={safetyCallFor(run, services, turn)}
      />
    </div>
  );
}

/**
 * One turn's two chat bubbles. The read is the same as the Conversation tab's
 * TurnCard: bodies the compact page already carries inline need none. A turn
 * reads its body once it is near the viewport and the gate has a free slot;
 * until then a light skeleton stands in for the bubbles.
 */
function TurnBubbles({
  projectId,
  previewRunRef,
  turn,
  showText,
  safetyCall,
}: {
  projectId: string;
  previewRunRef: string;
  turn: LabTurn;
  showText: boolean;
  /** Undefined until the run's services read is in. */
  safetyCall: PreviewServiceCall | null | undefined;
}) {
  const gate = useContext(TextGateContext);
  const voice = useLabVoice();
  const box = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(
    () => typeof IntersectionObserver === "undefined",
  );
  const turnEvent = turn.turnEvent;
  const gateId = `${previewRunRef}:${turn.turnNumber}`;
  const userInput = turnEvent?.detail.userInput ?? null;
  const livResponse = turnEvent?.detail.livResponse ?? null;
  const inlineAlready =
    (userInput === null || userInput.state !== "reference") &&
    (livResponse === null || livResponse.state !== "reference");
  const wantsRead = showText && turnEvent !== null && !inlineAlready;

  useEffect(() => {
    const node = box.current;
    if (!wantsRead || near || node === null) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [wantsRead, near]);

  useEffect(() => {
    if (wantsRead && near) {
      gate?.request(gateId);
    }
  }, [wantsRead, near, gate, gateId]);

  const mayRead =
    wantsRead && (gate === null || (near && gate.granted(gateId)));
  const detail = usePreviewRunEventQuery(
    projectId,
    previewRunRef,
    turnEvent?.eventRef ?? "",
    { enabled: mayRead, patient: true },
  );
  const settled = mayRead && !detail.isPending;
  useEffect(() => {
    if (settled) {
      gate?.release(gateId);
    }
  }, [settled, gate, gateId]);
  // A bubble that leaves while its read is running gives its slot back.
  const release = gate?.release;
  useEffect(() => () => release?.(gateId), [release, gateId]);

  const fetched = detail.data?.event;
  const fetchedDetail =
    typeof fetched === "object" && fetched !== null
      ? (fetched as Record<string, unknown>)["detail"]
      : undefined;
  const fromDetail = (name: "userInput" | "livResponse"): ContentSlot | null =>
    typeof fetchedDetail === "object" &&
    fetchedDetail !== null &&
    !Array.isArray(fetchedDetail)
      ? readContentSlot((fetchedDetail as Record<string, unknown>)[name])
      : null;
  const patientSlot = inlineAlready ? userInput : fromDetail("userInput");
  const livSlot = inlineAlready ? livResponse : fromDetail("livResponse");
  const livBody = livSlot === null ? null : readInlineBody(livSlot);
  const livText = livBody?.kind === "text" ? livBody.text : "";

  // A reply that arrives while the run is live is spoken once (when the
  // speaker is on); replies that were already there are only marked as seen.
  const announce = voice?.announce;
  const announceAfter = gate?.announceAfter ?? null;
  useEffect(() => {
    if (showText && livText !== "" && announceAfter !== null) {
      announce?.(gateId, livText, turn.turnNumber > announceAfter);
    }
  }, [showText, livText, announceAfter, announce, gateId, turn.turnNumber]);

  if (!showText) {
    return null;
  }
  if (turnEvent === null) {
    return (
      <p className="text-sm text-muted-foreground">Turn event not recorded</p>
    );
  }
  if (!inlineAlready && (detail.isPending || !mayRead)) {
    return (
      <div ref={box} aria-busy="true" className="flex flex-col gap-2">
        <span className="sr-only">Reading this turn…</span>
        <Skeleton className="me-auto h-12 w-3/5" />
        <Skeleton className="ms-auto h-12 w-3/5" />
      </div>
    );
  }
  if (detail.isError && !inlineAlready) {
    return (
      <p role="alert" className="text-sm text-destructive">
        This turn&apos;s text could not be read
        {describeDetailFailure(detail.error)}.{" "}
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={() => void detail.refetch()}
        >
          Try again
        </button>
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="me-auto max-w-[85%] rounded-lg bg-muted px-3 py-2">
        <Line label="Patient" slot={patientSlot} />
        {safetyCall !== undefined ? <SafetyBubbleBadge call={safetyCall} /> : null}
      </div>
      <div className="ms-auto flex max-w-[85%] items-start gap-1 rounded-lg bg-accent px-3 py-2">
        <div className="min-w-0 flex-1">
          <Line label="Liv" slot={livSlot} />
        </div>
        <PlayLineButton id={gateId} text={livText} />
      </div>
    </div>
  );
}
